/*This is like a cursor trail effect, that has like trail of circles following the cursor.
import React, { useEffect, useState, useRef } from "react";

export default function CoolCursorTrail() {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [trail, setTrail] = useState([]);
  const [hovering, setHovering] = useState(false);
  const trailRef = useRef([]);

  useEffect(() => {
    const handleMouseMove = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const checkHover = () => {
      const el = document.elementFromPoint(pos.x, pos.y);
      setHovering(el?.tagName === "A" || el?.onclick || el?.dataset?.clickable);
    };
    const interval = setInterval(checkHover, 30);
    return () => clearInterval(interval);
  }, [pos]);

  useEffect(() => {
    let animationFrame;

    const animate = () => {
      if (trailRef.current.length === 0) trailRef.current.push({ x: pos.x, y: pos.y, age: 0 });

      const newTrail = trailRef.current.map((p, i) => {
        const target = i === 0 ? pos : trailRef.current[i - 1];
        const dx = target.x - p.x;
        const dy = target.y - p.y;
        return { x: p.x + dx * 0.35, y: p.y + dy * 0.35, age: p.age + 1 };
      });

      newTrail.push({ x: pos.x, y: pos.y, age: 0 });
      if (newTrail.length > 25) newTrail.shift();

      trailRef.current = newTrail;
      setTrail([...trailRef.current]);

      animationFrame = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationFrame);
  }, [pos]);

  const generateTrailElements = () =>
    trail.map((p, i) => {
      const size = Math.max(2, 12 - p.age * 0.4);
      const alpha = Math.max(0, 1 - p.age * 0.05);
      const hue = (i * 10 + Date.now() * 0.02) % 360;

      return (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={size / 2}
          fill={`hsla(${hue}, 80%, 60%, ${alpha})`}
        />
      );
    });

  return (
    <>
      <svg
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      >
        {generateTrailElements()}
      </svg>

      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: hovering ? 28 : 12,
          height: hovering ? 28 : 12,
          transform: `translate(-50%, -50%) rotate(${hovering ? Math.sin(Date.now() * 0.01) * 20 : 0}deg)`,
          borderRadius: "50%",
          border: hovering ? "2px solid white" : "none",
          background: hovering ? "white" : "white",
          pointerEvents: "none",
          boxShadow: hovering ? "0 0 12px 4px white" : "0 0 4px 1px rgba(255,255,255,0.6)",
          transition: "width 0.15s, height 0.15s, border 0.15s, transform 0.15s",
          zIndex: 10000,
        }}
      />
    </>
  );
}
*/
import React, { useEffect, useState, useRef } from "react";

export default function CoolCursorTrail() {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const [scale, setScale] = useState(1);
  const trailRef = useRef([]);
  const [trail, setTrail] = useState([]);

  const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

  // track mouse position + hover detection
  useEffect(() => {
    if (isTouch) return; // skip on touch devices

    const handleMouseMove = (e) => {
      setPos({ x: e.clientX, y: e.clientY });

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const style = el && window.getComputedStyle(el);
      setHovering(
        style?.cursor === "pointer" ||
        el?.tagName === "A" ||
        el?.onclick ||
        el?.dataset?.clickable
      );
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isTouch]);

  // smooth bounce animation
  useEffect(() => {
    if (isTouch) return; // skip on touch devices

    let frame;
    let velocity = 0;
    let currentScale = scale;

    const animateBounce = () => {
      const target = hovering ? 1.3 : 1;
      const stiffness = 0.15;
      const damping = 0.75;

      const force = (target - currentScale) * stiffness;
      velocity = velocity * damping + force;
      currentScale += velocity;

      setScale(currentScale);
      frame = requestAnimationFrame(animateBounce);
    };

    animateBounce();
    return () => cancelAnimationFrame(frame);
  }, [hovering, isTouch]);

  // trail animation
  useEffect(() => {
    if (isTouch) return; // skip trail on touch

    let animationFrame;

    const animateTrail = () => {
      if (trailRef.current.length === 0) trailRef.current.push({ x: pos.x, y: pos.y });

      const newTrail = trailRef.current.map((p, i) => {
        const target = i === 0 ? pos : trailRef.current[i - 1];
        const dx = target.x - p.x;
        const dy = target.y - p.y;
        return { x: p.x + dx * 0.5, y: p.y + dy * 0.5 };
      });

      while (newTrail.length < 12) newTrail.push({ x: pos.x, y: pos.y });
      while (newTrail.length > 12) newTrail.shift();

      trailRef.current = newTrail;
      setTrail([...trailRef.current]);

      animationFrame = requestAnimationFrame(animateTrail);
    };

    animateTrail();
    return () => cancelAnimationFrame(animationFrame);
  }, [pos, isTouch]);

  const generatePath = (points) => {
    if (points.length < 2) return "";
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length - 2; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const midX = (p0.x + p1.x) / 2;
      const midY = (p0.y + p1.y) / 2;
      path += ` C ${p0.x} ${p0.y} ${p0.x} ${p0.y} ${midX} ${midY}`;
    }
    const last = points[points.length - 1];
    path += ` L ${last.x} ${last.y}`;
    return path;
  };

  if (isTouch) return null; // hide completely on touch

  return (
    <>
      <svg
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      >
        {trailRef.current.length >= 2 && (
          <path
            d={generatePath(trailRef.current)}
            stroke={!hovering ? "white" : "rgba(255, 255, 255, 0)"}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>

      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: hovering ? 14 : 8,
          height: hovering ? 14 : 8,
          transform: `translate(-50%, -50%) scale(${scale})`,
          borderRadius: "50%",
          border: hovering ? "2px solid white" : "none",
          background: hovering ? "transparent" : "white",
          pointerEvents: "none",
          boxShadow: hovering
            ? "inset 0 0 4px 2px white"
            : "0 0 6px 2px rgba(255,255,255,0.5)",
          zIndex: 10000,
        }}
      />
    </>
  );
}
